/**
 * Admin V3 — Bookings — Unassigned dispatch queue
 *
 * Mounted at: /admin-v3/bookings/unassigned (see AdminV3.js)
 *
 * GOVERNANCE MEANING: dispatch worklist for bookings awaiting a driver.
 * Migration of the legacy V1 "Courses à assigner" panel (AdminDashboard.js:1203-1231)
 * with its auto-assign + manual-assign actions — using the EXISTING backend
 * endpoints only (no backend change):
 *   - GET  /admin/bookings/unassigned      (queue feed, RBAC admin:bookings:unassigned)
 *   - GET  /admin/drivers/available        (assign dropdown, RBAC admin:drivers:available)
 *   - POST /admin/bookings/{id}/auto-assign (RBAC admin:bookings:auto_assign; Dispatch Engine, idempotent)
 *   - POST /admin/bookings/{id}/assign      (RBAC admin:bookings:assign; body {driver_id}, idempotent)
 *
 * Available drivers are fetched ONCE at page level and passed down (one fetch,
 * not one per row). Mutation controls HIDE-gate themselves on their permission.
 */

import React from 'react';
import { Zap, RefreshCw } from 'lucide-react';
import { SectionCard } from '../../shared/visualTokens';
import { Button } from '../../../components/ui/button';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import { t, DEFAULT_LOCALE } from '../../i18n';
import UnassignedQueueTable from './UnassignedQueueTable';

const asArray = (data) => (Array.isArray(data) ? data : (Array.isArray(data?.bookings) ? data.bookings : []));

const UnassignedQueuePage = () => {
  const locale = DEFAULT_LOCALE;

  const { data, loading, error, refetch } = useAdminQuery('/admin/bookings/unassigned');
  const driversQuery = useAdminQuery('/admin/drivers/available');

  const bookings = asArray(data);
  const availableDrivers = Array.isArray(driversQuery.data) ? driversQuery.data : [];
  const showSkeleton = loading && !data;

  const onDone = () => { refetch(); driversQuery.refetch(); };

  const headerAction = (
    <div className="flex items-center gap-2">
      {bookings.length > 0 && (
        <span
          className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700"
          data-testid="adminv3-unassigned-count"
        >
          {bookings.length}
        </span>
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={onDone}
        className="h-8 px-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        data-testid="adminv3-unassigned-refresh"
      >
        <RefreshCw className="h-4 w-4" />
      </Button>
    </div>
  );

  return (
    <SectionCard
      title={t('dispatch.title', locale)}
      icon={Zap}
      iconClass="bg-amber-100 text-amber-600"
      action={headerAction}
    >
      <UnassignedQueueTable
        bookings={bookings}
        availableDrivers={availableDrivers}
        loading={showSkeleton}
        error={error}
        onRetry={onDone}
        onDone={onDone}
        locale={locale}
      />
    </SectionCard>
  );
};

export default UnassignedQueuePage;
